import { motion } from 'framer-motion'
import { Eye } from 'lucide-react'
import { useState, useEffect } from 'react'
import { db } from '../firebase'
import { doc, onSnapshot, setDoc, updateDoc, increment, collection, addDoc, deleteDoc, getDocs, query, where } from 'firebase/firestore'

const LiveVisitorCounter = () => {
  const [visitors, setVisitors] = useState(0)

  useEffect(() => {
    const visitorDocRef = doc(db, 'analytics', 'liveVisitors')
    const sessionsRef = collection(db, 'analytics', 'liveVisitors', 'sessions')
    
    // Check if user already has an active session
    const existingSessionId = sessionStorage.getItem('visitorSessionId')
    const sessionId = existingSessionId || `visitor_${Date.now()}_${Math.random().toString(36).substring(7)}`
    let sessionDocRef = null
    
    // Increment visitor count only for new sessions
    const incrementVisitor = async () => {
      if (!existingSessionId) {
        try {
          // Add session document with timestamp
          sessionDocRef = await addDoc(sessionsRef, {
            sessionId: sessionId,
            joinedAt: Date.now(),
            lastActive: Date.now(),
            userAgent: navigator.userAgent.substring(0, 100)
          })
          
          // Store session info
          sessionStorage.setItem('visitorSessionId', sessionId)
          sessionStorage.setItem('visitorDocId', sessionDocRef.id)
          sessionStorage.setItem('visitorJoinedAt', Date.now().toString())
          
          console.log('New visitor session created:', sessionId)
        } catch (error) {
          console.error('Error incrementing visitor:', error)
          // Fallback to localStorage if Firebase fails
          const localCount = parseInt(localStorage.getItem('localVisitorCount') || '3') + 1
          localStorage.setItem('localVisitorCount', localCount.toString())
          setVisitors(localCount)
        }
      } else {
        // Reuse existing session
        const docId = sessionStorage.getItem('visitorDocId')
        if (docId) {
          sessionDocRef = doc(db, 'analytics', 'liveVisitors', 'sessions', docId)
          try {
            await updateDoc(sessionDocRef, {
              lastActive: Date.now()
            })
          } catch (err) {
            console.error('Error updating session:', err)
          }
        }
      }
    }
    
    incrementVisitor()

    // Cleanup old sessions (older than 5 minutes)
    const cleanupOldSessions = async () => {
      try {
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000)
        const oldSessionsQuery = query(sessionsRef, where('lastActive', '<', fiveMinutesAgo))
        const snapshot = await getDocs(oldSessionsQuery)
        
        const deletePromises = []
        snapshot.forEach((doc) => {
          deletePromises.push(deleteDoc(doc.ref))
        })
        
        if (deletePromises.length > 0) {
          await Promise.all(deletePromises)
          console.log(`Cleaned up ${deletePromises.length} old sessions`)
        }
      } catch (error) {
        console.error('Error cleaning up sessions:', error)
      }
    }

    // Run cleanup on mount and every 1 minute
    cleanupOldSessions()
    const cleanupInterval = setInterval(cleanupOldSessions, 60000)

    // Update lastActive every 30 seconds
    const updateInterval = setInterval(async () => {
      const docId = sessionStorage.getItem('visitorDocId')
      if (docId) {
        try {
          await updateDoc(doc(db, 'analytics', 'liveVisitors', 'sessions', docId), {
            lastActive: Date.now()
          })
        } catch (err) {
          console.error('Error updating lastActive:', err)
        }
      }
    }, 30000)

    // Listen to real-time session count
    let unsubscribe
    try {
      unsubscribe = onSnapshot(sessionsRef, (snapshot) => {
        const activeCount = snapshot.size
        setVisitors(Math.max(1, activeCount)) // Ensure at least 1 visitor (current user)
        console.log('Active visitors:', activeCount)
      }, (error) => {
        console.error('Error listening to visitors:', error)
        // Fallback to localStorage
        const localCount = parseInt(localStorage.getItem('localVisitorCount') || '3')
        setVisitors(localCount)
      })
    } catch (error) {
      console.error('Error setting up listener:', error)
      setVisitors(parseInt(localStorage.getItem('localVisitorCount') || '3'))
    }

    // Decrement visitor count on unmount (page close/navigation away)
    const handleBeforeUnload = async () => {
      const docId = sessionStorage.getItem('visitorDocId')
      const joinedAt = parseInt(sessionStorage.getItem('visitorJoinedAt') || '0')
      const timeOnSite = Date.now() - joinedAt
      
      // Only remove session if user was on site for more than 2 seconds (real visit)
      if (docId && timeOnSite > 2000) {
        try {
          await deleteDoc(doc(db, 'analytics', 'liveVisitors', 'sessions', docId))
          console.log('Session removed on exit')
        } catch (err) {
          console.error('Error removing session:', err)
        }
      }
    }
    
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      clearInterval(cleanupInterval)
      clearInterval(updateInterval)
      if (unsubscribe) unsubscribe()
      handleBeforeUnload()
      sessionStorage.removeItem('visitorSessionId')
      sessionStorage.removeItem('visitorDocId')
      sessionStorage.removeItem('visitorJoinedAt')
    }
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className="fixed bottom-24 right-6 z-40 bg-gradient-to-br from-dark/90 to-bronze-900/90 backdrop-blur-md border border-gold-500/30 rounded-full px-4 py-2 shadow-2xl"
    >
      <div className="flex items-center gap-2">
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Eye className="w-4 h-4 text-gold-400" />
        </motion.div>
        <div className="flex items-center gap-1">
          <motion.span
            key={visitors}
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-cream font-semibold text-sm"
          >
            {visitors}
          </motion.span>
          <span className="text-beige-300 text-xs">online</span>
        </div>
        <motion.div
          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-2 h-2 bg-green-400 rounded-full"
        />
      </div>
    </motion.div>
  )
}

export default LiveVisitorCounter
